import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEntitlements } from "@/hooks/use-entitlements";
import { UpgradeOverlay } from "@/components/upgrade-overlay";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { PortalLayout } from "@/components/portal-layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function fmtMoneyVN(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n || 0));
}

function FinanceReport({ months: monthsList }: { months: string[] }) {
  const dataQ = useQuery({
    queryKey: ["admin", "reports", "finance", monthsList.join(",")],
    queryFn: async () => {
      const [feesRes, salariesRes] = await Promise.all([
        supabase.from("student_fees").select("month, total_fee, paid_amount, is_paid").in("month", monthsList),
        supabase.from("teacher_salaries").select("month, salary_amount, is_paid").in("month", monthsList),
      ]);
      return { fees: feesRes.data ?? [], salaries: salariesRes.data ?? [] };
    },
  });

  const charts = React.useMemo(() => {
    const feesByMonth = new Map<string, { collected: number; total: number }>();
    const salByMonth = new Map<string, { paid: number; total: number }>();
    monthsList.forEach((m) => {
      feesByMonth.set(m, { collected: 0, total: 0 });
      salByMonth.set(m, { paid: 0, total: 0 });
    });
    (dataQ.data?.fees ?? []).forEach((f: any) => {
      const o = feesByMonth.get(f.month); if (!o) return;
      o.collected += Number(f.paid_amount ?? 0);
      o.total += Number(f.total_fee ?? 0);
    });
    (dataQ.data?.salaries ?? []).forEach((s: any) => {
      const o = salByMonth.get(s.month); if (!o) return;
      o.total += Number(s.salary_amount ?? 0);
      if (s.is_paid) o.paid += Number(s.salary_amount ?? 0);
    });
    const feesChart = monthsList.map((m) => {
      const v = feesByMonth.get(m)!;
      return { m: monthLabel(m), "Đã thu": v.collected, "Chưa thu": Math.max(0, v.total - v.collected) };
    });
    const salChart = monthsList.map((m) => {
      const v = salByMonth.get(m)!;
      return { m: monthLabel(m), "Đã trả": v.paid, "Chưa trả": Math.max(0, v.total - v.paid) };
    });
    const sum = {
      tuitionCollected: [...feesByMonth.values()].reduce((a, v) => a + v.collected, 0),
      tuitionTotal: [...feesByMonth.values()].reduce((a, v) => a + v.total, 0),
      salaryPaid: [...salByMonth.values()].reduce((a, v) => a + v.paid, 0),
      salaryTotal: [...salByMonth.values()].reduce((a, v) => a + v.total, 0),
    };
    return { feesChart, salChart, sum };
  }, [dataQ.data, monthsList]);

  const profit = charts.sum.tuitionCollected - charts.sum.salaryPaid;

  const exportFinance = async () => {
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(charts.feesChart), "Học phí");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(charts.salChart), "Lương GV");
      XLSX.writeFile(wb, `tai-chinh-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Đã xuất Excel");
    } catch (e: any) {
      toast.error("Không xuất được: " + (e?.message ?? ""));
    }
  };

  if (dataQ.isLoading) {
    return <div className="grid place-items-center py-20"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Đã thu học phí", value: `${fmtMoneyVN(charts.sum.tuitionCollected)}đ`, color: "text-emerald-600" },
          { label: "Còn nợ học phí", value: `${fmtMoneyVN(charts.sum.tuitionTotal - charts.sum.tuitionCollected)}đ`, color: "text-amber-600" },
          { label: "Đã trả lương", value: `${fmtMoneyVN(charts.sum.salaryPaid)}đ`, color: "text-foreground" },
          { label: "Lợi nhuận ước tính", value: `${fmtMoneyVN(profit)}đ`, color: profit >= 0 ? "text-emerald-600" : "text-red-600" },
        ].map((s) => (
          <div key={s.label} className="bg-card border rounded-2xl p-4">
            <div className="text-xs text-muted-foreground">{s.label}</div>
            <div className={`text-xl font-semibold mt-1 ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={exportFinance}>
          <FileSpreadsheet className="size-4" />Xuất Excel
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-card border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display text-lg font-semibold mb-1">Học phí theo tháng</h3>
          <p className="text-xs text-muted-foreground mb-3">Đã thu vs Chưa thu</p>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={charts.feesChart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.92 0.01 255)" />
                <XAxis dataKey="m" fontSize={11} axisLine={false} tickLine={false} />
                <YAxis fontSize={11} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v: any) => fmtMoneyVN(v) + "đ"} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Đã thu" stackId="f" fill="#4ADE80" />
                <Bar dataKey="Chưa thu" stackId="f" fill="#F87171" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display text-lg font-semibold mb-1">Lương GV theo tháng</h3>
          <p className="text-xs text-muted-foreground mb-3">Đã trả vs Chưa trả</p>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={charts.salChart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.92 0.01 255)" />
                <XAxis dataKey="m" fontSize={11} axisLine={false} tickLine={false} />
                <YAxis fontSize={11} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v: any) => fmtMoneyVN(v) + "đ"} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Đã trả" stackId="s" fill="#0F4C81" />
                <Bar dataKey="Chưa trả" stackId="s" fill="#FB923C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/admin/reports")({ component: ReportsPage });

type MonthKey = string; // "YYYY-MM"

function monthKey(d: Date): MonthKey {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(k: MonthKey) {
  const [, m] = k.split("-");
  return `T${parseInt(m, 10)}`;
}
function lastNMonths(n: number): MonthKey[] {
  const out: MonthKey[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(monthKey(d));
  }
  return out;
}

function ReportsPage() {
  const ent = useEntitlements();
  const [classId, setClassId] = React.useState<string>("all");
  const [months, setMonths] = React.useState<number>(6);

  if (!ent.reports && !ent.isLoading) {
    return (
      <PortalLayout
        portal="admin" title="Báo cáo"
        breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Báo cáo" }]}
      >
        <UpgradeOverlay
          feature="Báo cáo & Thống kê"
          description="Xem thống kê điểm danh, bài tập, tài chính theo tuần/tháng. Xuất Excel và PDF. Biểu đồ trực quan giúp bạn đưa ra quyết định tốt hơn."
        />
      </PortalLayout>
    );
  }


  const classesQ = useQuery({
    queryKey: ["admin", "reports", "classes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("classes").select("id, name").order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const monthsList = React.useMemo(() => lastNMonths(months), [months]);
  const rangeStart = React.useMemo(() => {
    const k = monthsList[0]; const [y, m] = k.split("-").map(Number);
    return new Date(y, m - 1, 1);
  }, [monthsList]);
  const rangeEnd = React.useMemo(() => {
    const k = monthsList[monthsList.length - 1];
    const [y, m] = k.split("-").map(Number);
    return new Date(y, m, 1);
  }, [monthsList]);

  const dataQ = useQuery({
    queryKey: ["admin", "reports", "data", classId, months],
    queryFn: async () => {
      // Sessions
      let sessQ = supabase.from("class_sessions")
        .select("id, class_id, starts_at, ends_at")
        .gte("starts_at", rangeStart.toISOString())
        .lt("starts_at", rangeEnd.toISOString());
      if (classId !== "all") sessQ = sessQ.eq("class_id", classId);
      const { data: sessions, error: sErr } = await sessQ;
      if (sErr) throw sErr;

      const sessionIds = (sessions ?? []).map((s) => s.id);

      // Attendances
      let attendances: any[] = [];
      if (sessionIds.length > 0) {
        const { data, error } = await supabase
          .from("attendances")
          .select("session_id, status")
          .in("session_id", sessionIds);
        if (error) throw error;
        attendances = data ?? [];
      }

      // Assignments
      let asgQ = supabase.from("assignments")
        .select("id, class_id, due_at, created_at")
        .gte("created_at", rangeStart.toISOString())
        .lt("created_at", rangeEnd.toISOString());
      if (classId !== "all") asgQ = asgQ.eq("class_id", classId);
      const { data: assignments, error: aErr } = await asgQ;
      if (aErr) throw aErr;

      const asgIds = (assignments ?? []).map((a) => a.id);

      // Submissions
      let submissions: any[] = [];
      if (asgIds.length > 0) {
        const { data, error } = await supabase
          .from("submissions")
          .select("assignment_id, submitted_at, score")
          .in("assignment_id", asgIds);
        if (error) throw error;
        submissions = data ?? [];
      }

      // Enrollments count per class (for missing submission calc)
      let enrollMap = new Map<string, number>();
      const classIds = Array.from(new Set((assignments ?? []).map((a) => a.class_id)));
      if (classIds.length) {
        const { data } = await supabase
          .from("enrollments").select("class_id").in("class_id", classIds).eq("is_active", true);
        (data ?? []).forEach((e) => {
          enrollMap.set(e.class_id, (enrollMap.get(e.class_id) ?? 0) + 1);
        });
      }

      return { sessions: sessions ?? [], attendances, assignments: assignments ?? [], submissions, enrollMap };
    },
  });

  // Aggregate per month
  const charts = React.useMemo(() => {
    if (!dataQ.data) {
      return {
        sessions: monthsList.map((k) => ({ m: monthLabel(k), taught: 0, untaught: 0 })),
        attendance: monthsList.map((k) => ({ m: monthLabel(k), present: 0, absent: 0, late: 0, excused: 0 })),
        submission: monthsList.map((k) => ({ m: monthLabel(k), ontime: 0, late: 0, missing: 0 })),
        scores: monthsList.map((k) => ({ m: monthLabel(k), avg: 0 })),
        summary: { total: 0, taught: 0, untaught: 0, attRate: 0 },
      };
    }


    const { sessions, attendances, assignments, submissions, enrollMap } = dataQ.data;
    const now = new Date();

    // Sessions taught vs untaught (only count past sessions for "untaught")
    const attSessions = new Set(attendances.map((a) => a.session_id));
    const sessByMonth = new Map<string, { taught: number; untaught: number; total: number }>();
    monthsList.forEach((k) => sessByMonth.set(k, { taught: 0, untaught: 0, total: 0 }));
    sessions.forEach((s) => {
      const k = monthKey(new Date(s.starts_at));
      const obj = sessByMonth.get(k); if (!obj) return;
      obj.total += 1;
      const past = new Date(s.starts_at) < now;
      if (attSessions.has(s.id)) obj.taught += 1;
      else if (past) obj.untaught += 1;
    });

    // Attendance status per month
    const attByMonth = new Map<string, { present: number; absent: number; late: number; excused: number }>();
    monthsList.forEach((k) => attByMonth.set(k, { present: 0, absent: 0, late: 0, excused: 0 }));
    const sessMonthMap = new Map(sessions.map((s) => [s.id, monthKey(new Date(s.starts_at))]));
    attendances.forEach((a) => {
      const k = sessMonthMap.get(a.session_id); if (!k) return;
      const obj = attByMonth.get(k); if (!obj) return;
      if (a.status === "present") obj.present += 1;
      else if (a.status === "absent") obj.absent += 1;
      else if (a.status === "late") obj.late += 1;
      else if (a.status === "excused") obj.excused += 1;
    });

    // Submissions: ontime vs late vs missing
    const subByMonth = new Map<string, { ontime: number; late: number; missing: number }>();
    monthsList.forEach((k) => subByMonth.set(k, { ontime: 0, late: 0, missing: 0 }));
    const subByAsg = new Map<string, any[]>();
    submissions.forEach((s) => {
      const arr = subByAsg.get(s.assignment_id) ?? [];
      arr.push(s); subByAsg.set(s.assignment_id, arr);
    });
    assignments.forEach((a) => {
      const k = monthKey(new Date(a.created_at));
      const obj = subByMonth.get(k); if (!obj) return;
      const subs = subByAsg.get(a.id) ?? [];
      const expectedStudents = enrollMap.get(a.class_id) ?? subs.length;
      subs.forEach((s) => {
        const submittedAt = new Date(s.submitted_at);
        if (a.due_at && submittedAt > new Date(a.due_at)) obj.late += 1;
        else obj.ontime += 1;
      });
      const missing = Math.max(0, expectedStudents - subs.length);
      obj.missing += missing;
    });

    // Avg scores per month
    const scoresByMonth = new Map<string, { sum: number; n: number }>();
    monthsList.forEach((k) => scoresByMonth.set(k, { sum: 0, n: 0 }));
    submissions.forEach((s) => {
      if (s.score == null) return;
      const k = monthKey(new Date(s.submitted_at));
      const obj = scoresByMonth.get(k); if (!obj) return;
      obj.sum += Number(s.score); obj.n += 1;
    });

    const sessionsChart = monthsList.map((k) => {
      const v = sessByMonth.get(k)!;
      return { m: monthLabel(k), taught: v.taught, untaught: v.untaught };
    });
    const attendanceChart = monthsList.map((k) => ({ m: monthLabel(k), ...attByMonth.get(k)! }));
    const submissionChart = monthsList.map((k) => ({ m: monthLabel(k), ...subByMonth.get(k)! }));
    const scoreChart = monthsList.map((k) => {
      const v = scoresByMonth.get(k)!;
      return { m: monthLabel(k), avg: v.n ? +(v.sum / v.n).toFixed(2) : 0 };
    });

    const total = sessions.length;
    const taught = sessions.filter((s) => attSessions.has(s.id)).length;
    const untaught = sessions.filter((s) => !attSessions.has(s.id) && new Date(s.starts_at) < now).length;
    const presentN = attendances.filter((a) => a.status === "present").length;
    const attRate = attendances.length ? Math.round((presentN / attendances.length) * 100) : 0;

    return {
      sessions: sessionsChart,
      attendance: attendanceChart,
      submission: submissionChart,
      scores: scoreChart,
      summary: { total, taught, untaught, attRate },
    };
  }, [dataQ.data, monthsList]);

  const isEmpty = !dataQ.isLoading
    && (charts.summary.total === 0)
    && (charts.attendance.every((r) => r.present + r.absent + r.late + r.excused === 0));

  const exportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(charts.sessions), "Buổi học");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(charts.attendance), "Điểm danh");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(charts.submission), "Nộp bài");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(charts.scores), "Điểm TB");
      const ts = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `bao-cao-${ts}.xlsx`);
      toast.success("Đã xuất Excel");
    } catch (e: any) {
      toast.error("Không xuất được Excel: " + (e?.message ?? ""));
    }
  };

  const exportPDF = () => window.print();

  return (
    <PortalLayout
      portal="admin" title="Báo cáo & Analytics"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Báo cáo" }]}
    >
      <Tabs defaultValue="academic">
        <TabsList className="mb-4">
          <TabsTrigger value="academic">Học tập</TabsTrigger>
          <TabsTrigger value="finance">Tài chính</TabsTrigger>
        </TabsList>
        <TabsContent value="finance">
          <FinanceReport months={monthsList} />
        </TabsContent>
        <TabsContent value="academic">
      <div className="flex flex-wrap gap-2 mb-6 print:hidden">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-52 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả lớp</SelectItem>
            {(classesQ.data ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(months)} onValueChange={(v) => setMonths(+v)}>
          <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 tháng gần đây</SelectItem>
            <SelectItem value="3">3 tháng gần đây</SelectItem>
            <SelectItem value="6">6 tháng gần đây</SelectItem>
            <SelectItem value="12">1 năm gần đây</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-1.5">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportPDF}>
            <FileText className="size-4" />PDF
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportExcel}>
            <FileSpreadsheet className="size-4" />Excel
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Tổng buổi", value: charts.summary.total, color: "text-foreground" },
          { label: "Đã dạy", value: charts.summary.taught, color: "text-emerald-600" },
          { label: "Chưa dạy", value: charts.summary.untaught, color: "text-amber-600" },
          { label: "Tỉ lệ điểm danh", value: `${charts.summary.attRate}%`, color: "text-[var(--portal)]" },
        ].map((s) => (
          <div key={s.label} className="bg-card border rounded-2xl p-4">
            <div className="text-xs text-muted-foreground">{s.label}</div>
            <div className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {dataQ.isLoading ? (
        <div className="grid place-items-center py-20 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : isEmpty ? (
        <div className="text-center py-20 text-muted-foreground border-2 border-dashed rounded-2xl">
          <FileText className="size-8 mx-auto mb-2 text-muted-foreground" />
          Chưa có dữ liệu
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {[
            { title: "Buổi học theo tháng", sub: "Đã dạy vs Chưa dạy",
              chart: (
                <BarChart data={charts.sessions}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.92 0.01 255)" />
                  <XAxis dataKey="m" fontSize={11} axisLine={false} tickLine={false} />
                  <YAxis fontSize={11} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="taught" stackId="a" name="Đã dạy" fill="#0F4C81" />
                  <Bar dataKey="untaught" stackId="a" name="Chưa dạy" fill="#FB923C" radius={[4,4,0,0]} />
                </BarChart>
              )},
            { title: "Điểm danh học sinh", sub: "Theo trạng thái",
              chart: (
                <BarChart data={charts.attendance}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.92 0.01 255)" />
                  <XAxis dataKey="m" fontSize={11} axisLine={false} tickLine={false} />
                  <YAxis fontSize={11} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="present" stackId="b" name="Có mặt" fill="#4ADE80" />
                  <Bar dataKey="late" stackId="b" name="Muộn" fill="#FACC15" />
                  <Bar dataKey="excused" stackId="b" name="Có phép" fill="#60A5FA" />
                  <Bar dataKey="absent" stackId="b" name="Vắng" fill="#F87171" radius={[4,4,0,0]} />
                </BarChart>
              )},
            { title: "Nộp bài tập", sub: "Đúng hạn / Trễ / Chưa nộp",
              chart: (
                <BarChart data={charts.submission}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.92 0.01 255)" />
                  <XAxis dataKey="m" fontSize={11} axisLine={false} tickLine={false} />
                  <YAxis fontSize={11} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="ontime" stackId="c" name="Đúng hạn" fill="#4ADE80" />
                  <Bar dataKey="late" stackId="c" name="Trễ" fill="#FACC15" />
                  <Bar dataKey="missing" stackId="c" name="Chưa nộp" fill="#F87171" radius={[4,4,0,0]} />
                </BarChart>
              )},
            { title: "Điểm trung bình", sub: "Theo tháng",
              chart: (
                <AreaChart data={charts.scores}>
                  <defs><linearGradient id="gsc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F4C81" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0F4C81" stopOpacity={0} />
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.92 0.01 255)" />
                  <XAxis dataKey="m" fontSize={11} axisLine={false} tickLine={false} />
                  <YAxis fontSize={11} axisLine={false} tickLine={false} domain={[0, 10]} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Area type="monotone" dataKey="avg" name="Điểm TB" stroke="#0F4C81" strokeWidth={2.5} fill="url(#gsc)" />
                </AreaChart>
              )},
          ].map((c, i) => (
            <motion.div key={c.title}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-card border rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <h3 className="font-display text-lg font-semibold">{c.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.sub}</p>
                </div>
              </div>
              <div className="h-64"><ResponsiveContainer>{c.chart}</ResponsiveContainer></div>
            </motion.div>
          ))}
        </div>
      )}
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
}
